/**
 * Cashflow Cockpit — lookup tables & formatters.
 *
 * Ported verbatim (as typed data) from the standalone HTML report's LABELMAP /
 * DEF / OSTATE / ORDER / ACT maps. Keeping these declarative means adding a new
 * label or state later is a one-line table edit, exactly like the original.
 */

import { AgingBucket, FollowupAction, InvoiceLabel, Lens, OutreachState } from './types';

/** Tailwind class tokens per chip "kind" — the second element of the HTML's LABELMAP. */
export type ChipKind = 'leak' | 'chase' | 'ok' | 'disp' | 'proof' | 'ap' | 'silent';

export const CHIP_KIND_CLASSES: Record<ChipKind, string> = {
	leak: 'bg-red-100 text-red-700 border-red-200',
	chase: 'bg-amber-100 text-amber-700 border-amber-200',
	ok: 'bg-green-100 text-green-700 border-green-200',
	disp: 'bg-purple-100 text-purple-700 border-purple-200',
	proof: 'bg-blue-100 text-blue-700 border-blue-200',
	ap: 'bg-gray-100 text-gray-600 border-gray-200',
	silent: 'bg-slate-200 text-slate-700 border-slate-300',
};

/** label → [display text, chip kind]. */
export const LABEL_MAP: Record<InvoiceLabel, [string, ChipKind]> = {
	customer_claims_paid_zoho_unpaid: ['LEAK', 'leak'],
	infinitybox_ack_paid_zoho_unpaid: ['WE-ACK-PAID', 'leak'],
	chase: ['CHASE', 'chase'],
	reconciled_ok: ['RECONCILED', 'ok'],
	disputed: ['DISPUTED', 'disp'],
	has_hard_proof: ['HARD PROOF', 'proof'],
	ap_we_wont_pay: ['AP·WONT-PAY', 'ap'],
	suppress_reminder: ['SUPPRESS', 'ap'],
	silent_invoice: ['SILENT', 'silent'],
	no_flag: ['NO FLAG', 'ap'],
	unconfirmed_match: ['UNCONFIRMED', 'ap'],
	audit_disagreement: ['⚠ AUDIT CORRECTS', 'leak'],
	audit_misclassified: ['⚠ MISCLASSIFIED', 'disp'],
};

/** Priority order for picking the *primary* label when grouping by state. */
export const LABEL_ORDER: InvoiceLabel[] = [
	'audit_disagreement',
	'audit_misclassified',
	'customer_claims_paid_zoho_unpaid',
	'infinitybox_ack_paid_zoho_unpaid',
	'disputed',
	'chase',
	'silent_invoice',
	'has_hard_proof',
	'reconciled_ok',
	'unconfirmed_match',
	'suppress_reminder',
	'no_flag',
];

/** Plain-language definition per label — shown as the chip tooltip. */
export const LABEL_DEF: Record<InvoiceLabel, string> = {
	customer_claims_paid_zoho_unpaid:
		'LEAK — the customer told us (by email) they have PAID this invoice, but Zoho still shows it unpaid. Money may already be in the bank, unrecorded. Verify the payment, then book it in Zoho.',
	infinitybox_ack_paid_zoho_unpaid:
		'WE-ACK-PAID — our own team acknowledged in email that this was paid, yet Zoho still shows it unpaid. Internal booking gap.',
	chase: "CHASE — unpaid, and the ball is in the client's court. A reminder is appropriate.",
	reconciled_ok:
		'RECONCILED — a payment claim exists AND Zoho confirms it paid. Books agree with email. Nothing to do.',
	disputed:
		'DISPUTED — the thread shows a dispute or legal/quality issue. Needs resolution, not a reminder.',
	has_hard_proof:
		'HARD PROOF — a UTR / NEFT / cheque reference (or a payment screenshot) backs the paid claim. Strongest evidence tier.',
	ap_we_wont_pay: 'AP — a payable we have decided not to pay.',
	suppress_reminder:
		'SUPPRESS — do NOT auto-send a reminder: the client says they paid. Nagging a client who paid is the worst outcome.',
	silent_invoice:
		'SILENT — an unpaid Zoho invoice with NO email trail at all. Nobody is chasing it. Open a conversation.',
	no_flag: 'NO FLAG — matched to Zoho but no triggering signal (often already paid, draft, or routine).',
	unconfirmed_match:
		'UNCONFIRMED — AI could not confirm this email thread is actually about this invoice. Treat with caution; not counted as a leak.',
	audit_disagreement:
		'AUDIT CORRECTS — the structured pipeline marked this paid/proven, but the full-thread forensic audit (Pro) found the claim is unverified or disputed. Trust the audit.',
	audit_misclassified:
		"MISCLASSIFIED — forensic audit found this thread isn't actually about this receivable (e.g. a payable, or a different invoice). The structured match was wrong.",
};

/** outreach state → [display, chip kind, tooltip]. */
export const OUTREACH: Record<OutreachState, [string, ChipKind, string]> = {
	awaiting_client: [
		'⏳ awaiting client',
		'ok',
		'We have done our part (invoice sent / chased). Ball is with the CLIENT — do NOT ask our team why this is pending.',
	],
	awaiting_client_no_reply: [
		'⏳ sent · no reply',
		'chase',
		'We sent it but the client has not responded at all. Ball with client — our team already acted.',
	],
	our_move: [
		'◄ our move',
		'leak',
		'The ball is on OUR side — we owe the next step. Legitimate internal action item.',
	],
	never_reached_out: [
		'✗ never contacted',
		'silent',
		'Nobody has reached out at all (no Zoho send, no email). Truly silent.',
	],
	in_progress: ['↔ in progress', 'proof', 'Active back-and-forth conversation.'],
	settled: ['✓ settled', 'ok', 'Paid / closed.'],
};

/** follow-up action → [display, color token]. */
export const ACTION_LABEL: Record<FollowupAction, [string, string]> = {
	verify_reconcile: ['◆ Verify', 'text-blue-600'],
	send: ['▲ Send', 'text-amber-600'],
	hold: ['● Hold', 'text-green-600'],
	open: ['▲ Open', 'text-amber-600'],
};

export const AGING_LABEL: Record<AgingBucket, string> = {
	'90+': '🔴 90+ days overdue',
	'60-90': '🟠 60–90 days',
	'30-60': '🟡 30–60 days',
	'0-30': '🟢 0–30 days overdue',
	not_due: '◷ not yet due',
	paid: '✓ paid',
};

export const RISK_LABEL: Record<string, string> = {
	'disputed/legal': '⚠ disputed / legal',
	'negative/worsening': '↘ negative / worsening',
	positive: '↗ positive',
	neutral: '· neutral',
};

/**
 * Proof / evidence-tier display. The API's raw enum (`unknown`, `hard_ref`,
 * `verbal_only`, `proof_mentioned`) reads as a bug to finance users — "unknown"
 * looks like missing data when it actually means "no payment evidence on file".
 * This maps each tier to a plain-language label + colour so the Proof column
 * states the *finding*, not the raw enum.
 */
export const PROOF_TIER: Record<string, { label: string; cls: string }> = {
	hard_ref: { label: 'UTR / hard ref', cls: 'bg-blue-50 text-blue-700' },
	proof_mentioned: { label: 'proof mentioned', cls: 'bg-sky-50 text-sky-700' },
	verbal_only: { label: 'verbal only', cls: 'bg-amber-50 text-amber-700' },
	unknown: { label: 'no proof', cls: 'bg-slate-100 text-slate-400' },
	none: { label: 'no proof', cls: 'bg-slate-100 text-slate-400' },
};

/** Resolve any tier string to its display (falls back to a cleaned raw value). */
export const proofTier = (tier?: string): { label: string; cls: string } =>
	PROOF_TIER[tier || 'unknown'] || {
		label: (tier || 'no proof').replace(/_/g, ' '),
		cls: 'bg-slate-100 text-slate-500',
	};

/** Verification-tier label for the "verify" lens. */
export const VTIER_LABEL: Record<string, string> = {
	both: '✓✓ doc proof + Zoho',
	email_doc: '✓ document proof',
	email_confirmed: '✓ email confirmed',
	zoho_only: '⚠ Zoho-only (no proof)',
	unverified: '· unverified',
	no_evidence: '· no payment evidence',
};

/** The lens chips shown above the table. */
export const LENSES: Array<{ id: Lens; label: string }> = [
	{ id: 'state', label: 'State' },
	{ id: 'client', label: 'Client' },
	{ id: 'kam', label: 'KAM' },
	{ id: 'month', label: 'Month' },
	{ id: 'aging', label: 'Aging' },
	{ id: 'risk', label: 'Risk' },
	{ id: 'outreach', label: 'Outreach' },
	{ id: 'verify', label: 'Verification' },
	{ id: 'tactics', label: 'Tactics' },
];

/** Fixed worst-first ordering for the aging lens groups. */
export const AGING_GROUP_ORDER: string[] = [
	'🔴 90+ days overdue',
	'🟠 60–90 days',
	'🟡 30–60 days',
	'🟢 0–30 days overdue',
	'◷ not yet due',
	'✓ paid',
];

// ── formatters (ported from the HTML's INR / INRc / D helpers) ──

/** Full rupee amount: ₹12,34,567 (Indian grouping). */
export const inr = (n: number | null | undefined): string =>
	n == null ? '—' : '₹' + Math.round(n).toLocaleString('en-IN');

/** Compact rupee: ₹1.23 Cr / ₹4.5 L / ₹6,789. */
export const inrCompact = (n: number | null | undefined): string => {
	const v = Math.round(n || 0);
	if (v >= 1e7) return '₹' + (v / 1e7).toFixed(2) + ' Cr';
	if (v >= 1e5) return '₹' + (v / 1e5).toFixed(1) + ' L';
	return '₹' + v.toLocaleString('en-IN');
};

/** Short date: 23 Apr 26. */
export const fmtDate = (s: string | null | undefined): string =>
	s
		? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })
		: '—';
